import React, { useCallback, useMemo, useState } from 'react';
import {
  AllocationMedium,
  SummaryResourceIdentifer,
} from 'api/resource-management/types';
import { formatter } from 'components/atoms/input-duration';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { useAggregationManager } from 'components/organisms/resource-management/managers';
import { DetailedAllocationData } from 'components/organisms/resource-management/types';
import { useTranslate } from 'hooks/use-translation';
import {
  useConvertResourceAllocationsQuery,
  useDeleteResourceAllocationsQuery,
} from 'queries/resource-management';

interface Props {
  resourceName: string;
  projectId: number;
  resourceIdentifier?: SummaryResourceIdentifer;
  onDeleteAllocations?: () => void;
  onConvertAllocations: (toSoft: boolean) => void;
}

interface ReturnProps {
  detailedAllocatedMinutes: DetailedAllocationData;
  onMenuVisibleChange: (visible: boolean) => void;
  showDeleteAllocationsConfirmation: () => void;
  showConvertAllocationsConfirmation: (toSoft: boolean) => void;
}

export const useAllocationsMoreActions = ({
  resourceName,
  projectId,
  resourceIdentifier,
  onDeleteAllocations,
  onConvertAllocations,
}: Props): ReturnProps => {
  const { mutateAsync: deleteAllocationsMutation } =
    useDeleteResourceAllocationsQuery();
  const { mutateAsync: convertAllocationsMutation } =
    useConvertResourceAllocationsQuery();

  const translate = useTranslate();
  const okText = translate('yes');
  const deleteText = translate('delete');

  const { getAllocatedMinutesFromAggregation } = useAggregationManager();

  const allocatedMinutes = useCallback(
    () => getAllocatedMinutesFromAggregation(projectId, resourceIdentifier),
    [getAllocatedMinutesFromAggregation, projectId, resourceIdentifier],
  );

  const [detailedAllocatedMinutes, setDetailedAllocatedMinutes] = useState(
    allocatedMinutes(),
  );

  const onMenuVisibleChange = useCallback(
    (visible: boolean) => {
      if (visible) {
        setDetailedAllocatedMinutes(allocatedMinutes());
      }
    },
    [allocatedMinutes],
  );

  const deleteAllocationsSuccessToast = useMemo(
    () => ({
      title: translate(
        'resource_management.actions.allocations.delete_all.success.title',
        {
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      ),
      message: translate(
        'resource_management.actions.allocations.delete_all.success.message',
        {
          resourceName,
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      ),
    }),
    [resourceName, translate],
  );

  const deleteAllocationsErrorToast = useMemo(
    () => ({
      title: translate(
        'resource_management.actions.allocations.delete_all.error.title',
        {
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      ),
      message: translate(
        'resource_management.actions.allocations.delete_all.error.message',
        {
          resourceName,
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      ),
    }),
    [resourceName, translate],
  );

  const deleteConfirmationModalTitle = translate(
    'resource_management.actions.allocations.delete_all.confirmation.title',
    {
      strong: (chunks) => <strong>{chunks}</strong>,
    },
  );

  const deleteConfirmationModalContent = useCallback(
    () =>
      translate(
        'resource_management.actions.allocations.delete_all.confirmation.message',
        {
          resourceName,
          hours: formatter(allocatedMinutes().totalAllocatedMinutes),
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      ),
    [allocatedMinutes, resourceName, translate],
  );

  const handleDeleteAllocations = useCallback(() => {
    return resourceIdentifier
      ? deleteAllocationsMutation({ projectId, resourceIdentifier })
          .then(() => {
            onDeleteAllocations?.();
            toastService(deleteAllocationsSuccessToast).success();
          })
          .catch(() => {
            toastService(deleteAllocationsErrorToast).error();
          })
      : new Promise(() => {});
  }, [
    deleteAllocationsErrorToast,
    deleteAllocationsMutation,
    deleteAllocationsSuccessToast,
    onDeleteAllocations,
    projectId,
    resourceIdentifier,
  ]);

  const showDeleteAllocationConfirmation = useCallback(() => {
    confirm({
      title: deleteConfirmationModalTitle,
      content: deleteConfirmationModalContent(),
      okType: 'danger',
      okText: deleteText,
      onOk: async () => {
        await handleDeleteAllocations();
      },
    });
  }, [
    deleteText,
    handleDeleteAllocations,
    deleteConfirmationModalContent,
    deleteConfirmationModalTitle,
  ]);

  const convertAllocationsSuccessToast = useCallback(
    (toSoft: boolean) => ({
      title: translate(
        'resource_management.actions.allocations.change_medium.success.title',
      ),
      message: translate(
        'resource_management.actions.allocations.change_medium.success.message',
        {
          resourceName,
          medium: `${
            toSoft ? AllocationMedium.Soft : AllocationMedium.Hard
          }`.toLowerCase(),
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      ),
    }),
    [resourceName, translate],
  );

  const convertAllocationsErrorToast = useCallback(
    (toSoft: boolean) => ({
      title: translate(
        'resource_management.actions.allocations.change_medium.error.title',
      ),
      message: translate(
        'resource_management.actions.allocations.change_medium.error.message',
        {
          resourceName,
          medium: `${
            toSoft ? AllocationMedium.Soft : AllocationMedium.Hard
          }`.toLowerCase(),
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      ),
    }),
    [resourceName, translate],
  );

  const convertAllocationsModalTitle = useCallback(
    (toSoft: boolean) =>
      translate(
        'resource_management.actions.allocations.change_medium.confirmation.title',
        {
          medium: `${
            toSoft ? AllocationMedium.Soft : AllocationMedium.Hard
          }`.toLowerCase(),
        },
      ),
    [translate],
  );
  const convertAllocationsModalContent = useCallback(
    (toSoft: boolean) =>
      translate(
        'resource_management.actions.allocations.change_medium.confirmation.message',
        {
          resourceName,
          hours: formatter(
            toSoft
              ? allocatedMinutes().hardAllocatedMinutes
              : allocatedMinutes().softAllocatedMinutes,
          ),
          fromMedium: `${
            toSoft ? AllocationMedium.Hard : AllocationMedium.Soft
          }`.toLowerCase(),
          toMedium: `${
            toSoft ? AllocationMedium.Soft : AllocationMedium.Hard
          }`.toLowerCase(),
          strong: (chunks) => <strong>{chunks}</strong>,
        },
      ),
    [allocatedMinutes, resourceName, translate],
  );

  const handleConvertAllocations = useCallback(
    (toSoft: boolean) =>
      convertAllocationsMutation({
        projectId,
        resourceIdentifier,
        medium: toSoft ? AllocationMedium.Soft : AllocationMedium.Hard,
      })
        .then(() => {
          onConvertAllocations(toSoft);
          toastService(convertAllocationsSuccessToast(toSoft)).success();
        })
        .catch(() => {
          toastService(convertAllocationsErrorToast(toSoft)).error();
        }),
    [
      convertAllocationsErrorToast,
      convertAllocationsMutation,
      convertAllocationsSuccessToast,
      onConvertAllocations,
      projectId,
      resourceIdentifier,
    ],
  );

  const showConvertAllocationsConfirmation = useCallback(
    (toSoft: boolean) => {
      confirm({
        title: convertAllocationsModalTitle(toSoft),
        content: convertAllocationsModalContent(toSoft),
        okText,
        onOk: async () => {
          await handleConvertAllocations(toSoft);
        },
      });
    },
    [
      convertAllocationsModalContent,
      convertAllocationsModalTitle,
      handleConvertAllocations,
      okText,
    ],
  );

  return {
    detailedAllocatedMinutes,
    onMenuVisibleChange,
    showDeleteAllocationsConfirmation: showDeleteAllocationConfirmation,
    showConvertAllocationsConfirmation,
  };
};
